<script setup>
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { commonSizes } from './size';
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { Plus, Check, X, Trash2, ChevronDown } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import Tooltip from '@/components/Tooltip2.vue';
import SizePreivew from './size/SizePreivew.vue';
import { $t } from '@/i18n';

const props = defineProps({
  class: String,
  open: Boolean,
});

const emit = defineEmits(['update:open']);

defineExpose({
  getSize,
  setSize,
});

function getSize() {
  const size = [...commonSizes, ...customSizes.value].find((s) => s.value === modelValue.value);
  if (!size) {
    return {
      w: commonSizes[0].width,
      h: commonSizes[0].height,
    };
  }
  return {
    w: size.width,
    h: size.height,
    height: size.autoHeight ? 'auto' : '',
  };
}

const modelValue = ref(commonSizes[0].value);
const isPopoverOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});
const selectedCategory = ref(null);
const customSizes = ref([]);
const showCustomForm = ref(false);
const customWidth = ref(null);
const customHeight = ref(null);
const customLabel = ref('');
const sizeListRef = ref(null);

function setSize(size) {
  if (size && size.w && size.h) {
    const matchedSize =
      commonSizes.find((s) => s.value === size.value) ||
      commonSizes.find((s) => s.width === size.w && s.height === size.h);

    if (matchedSize) {
      modelValue.value = matchedSize.value;
    } else {
      const matchedCustomSize = customSizes.value.find(
        (s) => s.width === size.w && s.height === size.h
      );
      if (matchedCustomSize) {
        modelValue.value = matchedCustomSize.value;
      } else {
        addCustomSize(size.w, size.h);
      }
    }
  }
}

function addCustomSize(width, height, label = '') {
  if (!width || !height) return;
  const customLabelText = label || `${width}×${height}`;
  const customValue = `custom_${Date.now()}`;
  const newCustomSize = {
    label: customLabelText,
    width: parseInt(width),
    height: parseInt(height),
    value: customValue,
    category: 'custom',
  };
  customSizes.value.push(newCustomSize);
  saveCustomSizes();
  modelValue.value = customValue;
  resetCustomForm();
}

function saveCustomSizes() {
  localStorage.setItem('custom:sizes', JSON.stringify(customSizes.value));
}

function resetCustomForm() {
  customWidth.value = null;
  customHeight.value = null;
  customLabel.value = '';
  showCustomForm.value = false;
}

function removeCustomSize(sizeValue) {
  customSizes.value = customSizes.value.filter((size) => size.value !== sizeValue);
  saveCustomSizes();
  if (modelValue.value === sizeValue) {
    modelValue.value = commonSizes[0].value;
  }
}

const cacheKey = 'create:size';
onMounted(() => {
  const savedCustomSizes = localStorage.getItem('custom:sizes');
  if (savedCustomSizes) {
    try {
      customSizes.value = JSON.parse(savedCustomSizes);
    } catch (e) {
      console.error('Failed to parse custom sizes', e);
      customSizes.value = [];
    }
  }
  const lastSelectedModel = localStorage.getItem(cacheKey);
  if (lastSelectedModel) {
    const existsInCommon = commonSizes.some((size) => size.value === lastSelectedModel);
    const existsInCustom = customSizes.value.some((size) => size.value === lastSelectedModel);
    if (existsInCommon || existsInCustom) {
      modelValue.value = lastSelectedModel;
    }
  }
});

watch(modelValue, (newValue) => {
  localStorage.setItem(cacheKey, newValue);
});

const selectedSize = computed(() => {
  const commonSize = commonSizes.find((size) => size.value === modelValue.value);
  if (commonSize) return commonSize;
  return customSizes.value.find((size) => size.value === modelValue.value) || commonSizes[0];
});

const categories = computed(() => {
  const groups = {};
  commonSizes.forEach((size) => {
    const category = size.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(size);
  });
  if (customSizes.value.length > 0) {
    if (!groups['custom']) {
      groups['custom'] = [];
    }
    customSizes.value.forEach((size) => {
      groups['custom'].push(size);
    });
  }
  return Object.entries(groups);
});

const filteredSizes = computed(() => {
  if (!selectedCategory.value) {
    return categories.value;
  }
  return categories.value.filter(([category]) => category === selectedCategory.value);
});

// Scroll to selected item
function scrollToSelectedItem() {
  nextTick(() => {
    setTimeout(() => {
      const sanitizedValue = modelValue.value.replace(/[^a-zA-Z0-9]/g, '_');
      const selectedElement = document.querySelector(`.size-item-${sanitizedValue}`);
      const scrollContainer = document.querySelector('.size-list-container');

      if (selectedElement && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;

        // Calculate the position to scroll to (center the element)
        const targetScrollTop =
          scrollTop +
          elementRect.top -
          containerRect.top -
          containerRect.height / 2 +
          elementRect.height / 2;

        scrollContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      }
    }, 100);
  });
}

watch(
  () => isPopoverOpen.value,
  (isOpen) => {
    if (isOpen && !selectedCategory.value && categories.value.length > 0) {
      const currentSize = selectedSize.value;
      if (currentSize && currentSize.category) {
        selectedCategory.value = currentSize.category;
      } else {
        selectedCategory.value = categories.value[0][0];
      }
      // Scroll to selected item after category is set
      scrollToSelectedItem();
    }
  }
);

// Also scroll when category changes
watch(selectedCategory, () => {
  if (isPopoverOpen.value) {
    scrollToSelectedItem();
  }
});

const displaySizeText = computed(() => {
  const sizeLabel = $t(`create.sizes.${selectedSize.value.value}`, selectedSize.value.label);
  if (selectedSize.value.physicalDimensions) {
    return `${sizeLabel} (${selectedSize.value.physicalDimensions.width}×${selectedSize.value.physicalDimensions.height}${selectedSize.value.physicalDimensions.unit})`;
  }
  return `${sizeLabel} (${selectedSize.value.width} × ${
    selectedSize.value.autoHeight ? $t('create.sizes.auto') : selectedSize.value.height
  })`;
});

// 计算触发按钮中的尺寸预览图标大小
const triggerPreviewSize = computed(() => {
  const maxSize = 14;
  const w = selectedSize.value.width;
  const h = selectedSize.value.height;
  const scale = maxSize / Math.max(w, h);
  return {
    width: `${Math.round(w * scale)}px`,
    height: `${Math.round(h * scale)}px`,
  };
});

function handleSizeItemMouseEnter(event, sizeValue) {
  if (modelValue.value !== sizeValue) {
    event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  }
}

function handleSizeItemMouseLeave(event, sizeValue) {
  if (modelValue.value !== sizeValue) {
    event.currentTarget.style.backgroundColor = '';
  }
}
</script>

<template>
  <div class="flex w-full">
    <Tooltip :text="displaySizeText">
      <Popover v-model:open="isPopoverOpen">
        <PopoverTrigger
          :class="
            cn(
              `seede-size-selector group flex items-center gap-1 w-full h-11 px-1.5 py-2 bg-white rounded-full border border-black/5 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20 transition-all duration-300 ease-out text-sm outline-none focus-visible:ring-2 focus-visible:ring-black/5 focus-visible:border-black/10`,
              props.class
            )
          "
          tabindex="0"
          @keydown.enter.prevent="isPopoverOpen = !isPopoverOpen"
          aria-label="size"
        >
          <div class="flex items-center gap-0 shrink-0 overflow-hidden flex-1">
            <div class="shrink-0 size-6 flex items-center justify-center">
              <div
                class="bg-black/40 group-hover:bg-black/60 rounded-[2px] transition-colors"
                :style="triggerPreviewSize"
              ></div>
            </div>

            <span
              class="text-xs text-black/60 group-hover:text-black/90 transition-colors truncate"
            >
              <template v-if="selectedSize.physicalDimensions">
                {{ displaySizeText }}
              </template>
              <template v-else>
                {{ selectedSize.width }} ×
                {{ selectedSize.autoHeight ? $t(`create.sizes.auto`) : selectedSize.height }}
              </template>
            </span>
          </div>
          <ChevronDown
            class="size-4 transition-transform duration-200 text-black/30 group-hover:text-black/90"
          />
        </PopoverTrigger>
        <PopoverContent
          class="size-selector-dropdown w-[560px] h-[380px] p-1 rounded-[20px] backdrop-blur-[60px]"
          style="
            background-color: rgba(255, 255, 255, 0.85);
            border: 1px solid rgba(0, 0, 0, 0.07);
            box-shadow:
              0 20px 25px -5px rgba(0, 0, 0, 0.1),
              0 8px 10px -6px rgba(0, 0, 0, 0.1);
          "
          :collisionPadding="16"
        >
          <div class="flex h-full">
            <!-- Categories on the left -->
            <div class="w-[200px] shrink-0 p-2 flex flex-col gap-0.5">
              <div
                v-for="[category] in categories"
                :key="category"
                class="px-3 py-2.5 text-sm font-normal text-black/50 hover:text-black/90 cursor-pointer transition-all duration-200 rounded-lg select-none group"
                :class="{
                  'text-black/90 font-normal shadow-base': selectedCategory === category,
                }"
                :style="
                  selectedCategory === category ? 'background-color: rgba(0, 0, 0, 0.05)' : ''
                "
                @mouseenter="$event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'"
                @mouseleave="
                  $event.currentTarget.style.backgroundColor =
                    selectedCategory === category ? 'rgba(0, 0, 0, 0.05)' : ''
                "
                @click="selectedCategory = category"
                tabindex="0"
                @keydown.enter.prevent="selectedCategory = category"
              >
                <div class="flex items-center gap-2">
                  {{ $t(`create.size_categories.${category.toLowerCase()}`, category) }}
                </div>
              </div>
              <!-- Add custom size button -->
              <div
                class="mt-auto w-full relative rounded-lg transition-colors duration-200 p-3 cursor-pointer hover:bg-black/[0.03]"
                @click="
                  showCustomForm = true;
                  selectedCategory = 'custom';
                "
                tabindex="0"
                @keydown.enter.prevent="
                  showCustomForm = true;
                  selectedCategory = 'custom';
                "
              >
                <div class="flex items-center gap-3">
                  <div class="w-4 h-4 rounded-full flex items-center justify-center">
                    <Plus class="w-4 h-4 text-black/40" />
                  </div>
                  <span class="text-sm font-normal text-black/60">
                    {{ $t('create.size_categories.add_custom') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Size list on the right -->
            <div
              ref="sizeListRef"
              class="size-list-container grow p-2 h-full overflow-y-auto custom-scrollbar"
            >
              <!-- Custom size form -->
              <transition name="slide-fade">
                <div v-if="showCustomForm" class="px-4 py-3 border border-black/10 rounded-xl mb-2">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-sm font-medium text-black/90 flex items-center gap-2">
                      {{ $t('create.custom_size.title') }}
                    </h3>
                    <button
                      @click="resetCustomForm"
                      class="text-black/40 hover:text-black/70 p-1.5 rounded-lg hover:bg-black/10 transition-all duration-200"
                    >
                      <X class="size-4" />
                    </button>
                  </div>
                  <div class="space-y-3.5">
                    <div>
                      <label class="text-xs text-black/60 font-medium mb-1.5 block">{{
                        $t('create.custom_size.name')
                      }}</label>
                      <input
                        v-model="customLabel"
                        type="text"
                        class="w-full px-3 py-2.5 text-sm border border-black/10 rounded-lg focus:border-black/40 focus:ring-2 focus:ring-black/5 outline-none bg-white transition-all duration-200"
                        :placeholder="$t('create.custom_size.name_placeholder')"
                      />
                    </div>
                    <div class="flex gap-3">
                      <div class="flex-1">
                        <label class="text-xs text-black/60 font-medium mb-1.5 block">{{
                          $t('create.custom_size.width')
                        }}</label>
                        <input
                          v-model="customWidth"
                          type="number"
                          min="1"
                          class="w-full px-3 py-2.5 text-sm border border-black/10 rounded-lg focus:border-black/40 focus:ring-2 focus:ring-black/5 outline-none shadow-sm bg-white transition-all duration-200"
                        />
                      </div>
                      <div class="flex-1">
                        <label class="text-xs text-black/60 font-medium mb-1.5 block">{{
                          $t('create.custom_size.height')
                        }}</label>
                        <input
                          v-model="customHeight"
                          type="number"
                          min="1"
                          class="w-full px-3 py-2.5 text-sm border border-black/10 rounded-lg focus:border-black/40 focus:ring-2 focus:ring-black/5 outline-none shadow-sm bg-white transition-all duration-200"
                        />
                      </div>
                    </div>
                    <div class="flex gap-2.5 justify-end pt-1">
                      <button
                        @click="resetCustomForm"
                        class="px-4 py-2 text-sm font-medium text-black/60 hover:text-black/90 hover:bg-black/5 rounded-lg transition-all duration-200"
                      >
                        {{ $t('create.custom_size.cancel') }}
                      </button>
                      <button
                        @click="addCustomSize(customWidth, customHeight, customLabel)"
                        class="px-4 py-2 text-sm font-medium text-white bg-black/90 hover:bg-black/80 rounded-lg transition-all duration-200 flex items-center gap-2 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-black/90"
                        :disabled="!customWidth || !customHeight"
                      >
                        <Check class="size-4" />
                        {{ $t('create.custom_size.create') }}
                      </button>
                    </div>
                  </div>
                </div>
              </transition>

              <div v-for="[category, sizes] in filteredSizes" :key="category" class="mb-3 px-1">
                <div
                  v-for="size in sizes"
                  :key="size.value"
                  :class="[
                    'size-item-' + size.value.replace(/[^a-zA-Z0-9]/g, '_'),
                    'px-3.5 py-3 cursor-pointer transition-all duration-200 rounded-lg mb-0.5 outline-none focus-visible:ring-2 focus-visible:ring-black/90 group',
                  ]"
                  :style="modelValue === size.value ? 'background-color: rgba(0, 0, 0, 0.05)' : ''"
                  @mouseenter="handleSizeItemMouseEnter($event, size.value)"
                  @mouseleave="handleSizeItemMouseLeave($event, size.value)"
                  @click="
                    modelValue = size.value;
                    isPopoverOpen = false;
                  "
                  :tabindex="0"
                  @keydown.enter.prevent="
                    modelValue = size.value;
                    isPopoverOpen = false;
                  "
                >
                  <div class="flex justify-between gap-3 items-center w-full">
                    <div class="flex items-center gap-3 min-w-0 flex-1">
                      <SizePreivew
                        :width="size.width"
                        :height="size.height"
                        :selected="modelValue === size.value"
                      />

                      <div class="flex flex-col min-w-0 flex-1">
                        <span
                          class="text-sm font-normal group-hover:text-black/90 transition-colors duration-200 truncate"
                          :class="{
                            'text-black': modelValue === size.value,
                            'text-black/50': modelValue !== size.value,
                          }"
                        >
                          {{
                            size.category === 'custom'
                              ? size.label
                              : $t(`create.sizes.${size.value}`, size.label)
                          }}
                        </span>
                        <span
                          class="text-xs font-normal transition-all duration-200"
                          :class="{
                            'text-black': modelValue === size.value,
                            'text-black/50': modelValue !== size.value,
                          }"
                        >
                          <template v-if="size.physicalDimensions">
                            {{ size.physicalDimensions.width }}×{{ size.physicalDimensions.height
                            }}{{ size.physicalDimensions.unit }}
                          </template>
                          <template v-else>
                            {{ size.width }} ×
                            {{ size.autoHeight ? $t(`create.sizes.auto`) : size.height }}
                          </template>
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                      <!-- Check icon for selected size -->
                      <div v-if="modelValue === size.value" class="shrink-0">
                        <Check class="size-5 text-black/60" />
                      </div>
                      <!-- Delete button for custom sizes -->
                      <button
                        v-if="size.category === 'custom'"
                        @click.stop="removeCustomSize(size.value)"
                        class="text-black/50 hover:text-red-600 transition-all duration-200 p-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 class="size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="filteredSizes.length === 0"
                class="text-center text-black/50 py-12 text-sm"
              >
                {{ $t('create.size_categories.empty') }}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </Tooltip>
  </div>
</template>

<style scoped>
.seede-size-selector .size-selector-label {
  display: none;
}
.seede-size-selector .size-selector-size {
  background: none;
  padding: 0;
}
.seede-size-selector .size-selector-list {
  gap: 0;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  margin: 8px 0;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  transition: background 0.2s;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.15);
}

.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.slide-fade-enter-from {
  transform: translateY(-8px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-4px);
  opacity: 0;
}
</style>
